"use client";

import React from "react";
import { Box, Typography, Grid, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { motion } from "framer-motion";
import TimeSlotCard from "./TimeSlotCard";
import TimezoneSelector from "@/app/utils/TimezoneSelector";
import { useTimezone } from "@/app/utils/TimezoneContext";

const TimeSlotGrid = ({
  theme,
  timeSlots,
  selectedTime,
  isToday,
  handleTimeSelect,
  userTimezone,
  selectedDay,
  duration,
  currentPage,
  setCurrentPage,
  slotsPerPage = 8,
  showPagination = true,
  showTimezoneSelector = true,
  disableTimezone = false,
  slotColumnSize = 6,
  slotCardSx = {},
  slotTextSx = {},
  timezoneSectionSx = {},
  timezoneSelectorSx = {},
  timezonePrefixText = "",
  paginationSx = {},
  slotsMinHeight = 100,
}) => {
  const { setManualTimezone } = useTimezone();
  const tinted = Boolean(theme?.palette?.tintedSurface);
  const navButtonSx = tinted
    ? {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.text.primary, 0.25)}`,
        "&:hover": {
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.text.primary,
        },
      }
    : {
        backgroundColor: theme.palette.grey[100],
        "&:hover": { backgroundColor: theme.palette.grey[200] },
      };
  const dotColors = tinted
    ? {
        active: theme.palette.text.primary,
        activeHover: theme.palette.text.primary,
        idle: alpha(theme.palette.text.primary, 0.25),
        idleHover: alpha(theme.palette.text.primary, 0.45),
      }
    : {
        active: theme.palette.primary.main,
        activeHover: theme.palette.primary.dark,
        idle: theme.palette.grey[300],
        idleHover: theme.palette.grey[400],
      };

  const paginatedTimeSlots = React.useMemo(() => {
    if (!timeSlots || timeSlots.length === 0)
      return { slots: [], totalPages: 0 };

    const totalPages = Math.ceil(timeSlots.length / slotsPerPage);
    const startIndex = currentPage * slotsPerPage;
    const endIndex = startIndex + slotsPerPage;
    const slots = timeSlots.slice(startIndex, endIndex);

    return { slots, totalPages };
  }, [timeSlots, currentPage, slotsPerPage]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) =>
      Math.min(paginatedTimeSlots.totalPages - 1, prev + 1),
    );
  };

  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50;

    if (paginatedTimeSlots.totalPages <= 1) return;

    if (info.offset.x > swipeThreshold) {
      handlePrevPage();
    } else if (info.offset.x < -swipeThreshold) {
      handleNextPage();
    }
  };

  const handleTimezoneChange = (newTimezone) => {
    if (disableTimezone) return;
    setManualTimezone(newTimezone);
    setCurrentPage(0);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 0,
          mt: 0.5,
          ...timezoneSectionSx,
        }}
      >
        {showTimezoneSelector && timezonePrefixText ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
            }}
          >
            {timezonePrefixText}
          </Typography>
        ) : null}
        {showTimezoneSelector && (
          <TimezoneSelector
            currentTimezone={userTimezone}
            onTimezoneChange={handleTimezoneChange}
            disabled={disableTimezone}
            sx={{ flexShrink: 0, ...timezoneSelectorSx }}
          />
        )}
      </Box>
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.1}
        onDragEnd={handleDragEnd}
        style={{
          minHeight: slotsMinHeight,
          cursor: paginatedTimeSlots.totalPages > 1 ? "grab" : "default",
        }}
        whileDrag={{ cursor: "grabbing" }}
      >
        <Box sx={{ minHeight: slotsMinHeight }}>
          {paginatedTimeSlots.slots.length > 0 ? (
            <Grid container spacing={1} sx={{ width: "100%" }}>
              {paginatedTimeSlots.slots.map((timeSlot, index) => (
                <TimeSlotCard
                  key={`timeslot-${currentPage}-${index}`}
                  timeSlot={timeSlot}
                  theme={theme}
                  selectedTime={selectedTime}
                  isToday={isToday}
                  handleTimeSelect={handleTimeSelect}
                  userTimezone={userTimezone}
                  selectedDay={selectedDay}
                  columnSize={slotColumnSize}
                  cardSx={slotCardSx}
                  textSx={slotTextSx}
                />
              ))}
            </Grid>
          ) : selectedDay ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 120,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                No available time slots for {duration} minute meetings on this
                day.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 120,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Select a date with availability to view time slots.
              </Typography>
            </Box>
          )}
        </Box>
      </motion.div>
      {showPagination && paginatedTimeSlots.totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 0.5,
            mb: 2.5,
            gap: 2,
            ...paginationSx,
          }}
        >
          <IconButton
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            size="small"
            sx={{
              ...navButtonSx,
              width: 32,
              height: 32,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {Array.from({ length: paginatedTimeSlots.totalPages }).map(
              (_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentPage(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      currentPage === index ? dotColors.active : dotColors.idle,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        currentPage === index
                          ? dotColors.activeHover
                          : dotColors.idleHover,
                      transform: "scale(1.2)",
                    },
                  }}
                />
              ),
            )}
          </Box>

          <IconButton
            onClick={handleNextPage}
            disabled={currentPage === paginatedTimeSlots.totalPages - 1}
            size="small"
            sx={{
              ...navButtonSx,
              width: 32,
              height: 32,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </>
  );
};

export default TimeSlotGrid;
